import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { DiscordIcon } from "@/components/discord";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  ArrowRight, Link2, LayoutDashboard, Download, Wallet, Sparkles, Check,
  Zap, Twitter, Instagram, Github, Users, Star, ShieldCheck,
} from "lucide-react";

const PLATFORM_FEE_PCT = 0.135;
const PAYOUT_FEE = 2.0;

function PayoutSimulator() {
  const [subs, setSubs] = useState(42);
  const [price, setPrice] = useState(10);
  const gross = subs * price;
  const platformFee = gross * PLATFORM_FEE_PCT;
  const fee = platformFee + PAYOUT_FEE;
  const net = Math.max(0, gross - fee);
  return (
    <div className="card-pop bg-white p-6 sm:p-8">
      <p className="eyebrow text-violet-600 mb-1">Payout Projector</p>
      <h3 className="font-display text-2xl font-extrabold text-[#111114] mb-6">See what you keep</h3>
      <div className="space-y-6 mb-6">
        <div>
          <div className="flex justify-between mb-2">
            <span className="text-sm font-semibold text-slate-600">Subscribers</span>
            <span className="font-mono font-bold text-[#111114]" data-testid="sim-subs">{subs}</span>
          </div>
          <Slider value={[subs]} min={0} max={300} step={1} onValueChange={(v) => setSubs(v[0])} data-testid="sim-subs-slider" />
        </div>
        <div>
          <div className="flex justify-between mb-2">
            <span className="text-sm font-semibold text-slate-600">Price / month</span>
            <span className="font-mono font-bold text-[#111114]">${price}</span>
          </div>
          <Slider value={[price]} min={1} max={50} step={1} onValueChange={(v) => setPrice(v[0])} data-testid="sim-price-slider" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl border-2 border-[#111114] bg-cyan-100 p-3">
          <p className="text-[10px] font-mono uppercase tracking-wide text-slate-600">Gross</p>
          <p className="font-display text-lg font-extrabold text-[#111114]">${gross.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border-2 border-[#111114] bg-rose-100 p-3">
          <p className="text-[10px] font-mono uppercase tracking-wide text-slate-600">Fees</p>
          <p className="font-display text-lg font-extrabold text-[#111114]">-${fee.toFixed(0)}</p>
        </div>
        <div className="rounded-xl border-2 border-[#111114] bg-lime-200 p-3">
          <p className="text-[10px] font-mono uppercase tracking-wide text-slate-600">You keep</p>
          <p className="font-display text-lg font-extrabold text-[#111114]" data-testid="sim-net">${net.toLocaleString()}</p>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-4">13.5% platform fee on sales, plus a flat ${PAYOUT_FEE.toFixed(2)} fee per payout. Paid out via PayPal in NZD.</p>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);

  useEffect(() => {}, []);

  const features = [
    { icon: Link2, title: "Link-in-bio storefront", desc: "A bold, shareable page at your own handle. Drop it anywhere your people hang out.", color: "bg-violet-200" },
    { icon: Wallet, title: "Recurring memberships", desc: "Members subscribe to your tiers with one-tap Discord login and secure card checkout.", color: "bg-lime-200" },
    { icon: Download, title: "Digital products", desc: "Sell downloadable files — presets, guides, packs. Buyers get instant, gated access.", color: "bg-cyan-200" },
    { icon: LayoutDashboard, title: "Twin dashboards", desc: "Command-center for creators, effortless billing for members. Everyone gets their view.", color: "bg-rose-200" },
    { icon: Wallet, title: "Payouts you understand", desc: "A crystal-clear breakdown of gross, fees and net. Flat 13.5% platform fee plus a $2 payout fee.", color: "bg-amber-200" },
    { icon: Sparkles, title: "Discord-native", desc: "No passwords. Your community signs in with the account they already use.", color: "bg-violet-200" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#111114] overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative dotted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="chip bg-lime-300 inline-flex items-center gap-1.5 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Flat 13.5% fee · keep the rest
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1] mb-6">
              Your community,<br /><span className="text-violet-600">your storefront,</span><br />your money.
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
              VangTech Subs gives creators & community owners a bold link-in-bio to sell memberships and digital downloads. Share one link, get paid monthly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-violet-600 hover:bg-violet-500 text-white btn-pop rounded-full h-13 px-8 text-base font-bold"
                onClick={() => navigate("/auth?role=creator")} data-testid="hero-creator-cta">
                Launch your storefront <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button size="lg" className="bg-white text-[#111114] btn-pop rounded-full h-13 px-8 text-base font-bold hover:bg-cyan-50"
                onClick={() => navigate("/auth?role=member")} data-testid="hero-member-cta">
                Join as a member
              </Button>
            </div>
            <div className="flex items-center gap-5 mt-8 text-sm font-semibold text-slate-600">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-violet-600" strokeWidth={3} /> No setup fees</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-violet-600" strokeWidth={3} /> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-violet-600" strokeWidth={2.5} /> Secured by Stripe</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}>
            <PayoutSimulator />
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y-2 border-[#111114] bg-violet-600 py-3 overflow-hidden">
        <div className="marquee-track">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-8 pr-8 whitespace-nowrap">
              {["MEMBERSHIPS", "DIGITAL DOWNLOADS", "LINK-IN-BIO", "13.5% FLAT FEE", "$2 PAYOUT FEE", "DISCORD LOGIN", "PAYPAL PAYOUTS"].map((t) => (
                <span key={t} className="font-mono text-sm font-bold text-lime-300 flex items-center gap-8">{t} <Star className="w-3.5 h-3.5 fill-lime-300" /></span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl mb-12">
          <p className="eyebrow text-violet-600 mb-3">Everything you need</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">Built for the whole journey</h2>
          <p className="text-slate-600 text-lg">From your first subscriber to your ten-thousandth — VangTech Subs scales with you.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }} className="card-pop card-pop-hover bg-white p-7">
              <div className={`w-12 h-12 rounded-xl ${f.color} border-2 border-[#111114] flex items-center justify-center mb-5`}>
                <f.icon className="w-6 h-6 text-[#111114]" />
              </div>
              <h3 className="font-display text-xl font-extrabold mb-2">{f.title}</h3>
              <p className="text-slate-600 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Discover creators */}
      {creators.length > 0 && (
        <section className="bg-white border-y-2 border-[#111114]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <p className="eyebrow text-violet-600 mb-3">Discover</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-10">Creators on VangTech Subs</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {creators.slice(0, 6).map((c) => (
                <button key={c.handle} onClick={() => navigate(`/s/${c.handle}`)} data-testid={`discover-${c.handle}`}
                  className="card-pop card-pop-hover bg-[#FAF9F6] overflow-hidden text-left">
                  <div className="h-28 bg-cover bg-center border-b-2 border-[#111114]" style={{ backgroundImage: `url(${c.banner_url})` }} />
                  <div className="p-5 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl border-2 border-[#111114] overflow-hidden bg-violet-200 flex items-center justify-center -mt-10 shrink-0">
                      {c.avatar_url ? <img src={c.avatar_url} alt={c.display_name} className="w-full h-full object-cover" /> : <span className="font-display font-bold">{c.display_name[0]}</span>}
                    </div>
                    <div className="min-w-0">
                      <p className="font-display font-extrabold truncate">{c.display_name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> {c.subscriber_count} subscribers</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="card-pop bg-violet-600 p-10 sm:p-16 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-5">
            Keep more of what you earn — <span className="text-lime-300">just 13.5%</span>
          </h2>
          <p className="text-violet-100 text-lg mb-8 max-w-xl mx-auto">No setup fees, no upfront cost. A flat 13.5% platform fee plus a $2 payout fee — that's it. Start building your subscription business in minutes.</p>
          <Button size="lg" className="bg-lime-300 hover:bg-lime-200 text-[#111114] btn-pop rounded-full h-13 px-10 text-base font-bold"
            onClick={() => navigate("/auth?role=creator")} data-testid="cta-get-started">
            <DiscordIcon className="w-5 h-5 mr-2" /> Start with Discord
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
